import { AbstractControl, FormArray, FormGroup, ValidationErrors } from '@angular/forms';
import { REQUIRED_STATUS } from '@shared/constants/shared.constants';

export interface FormErrorEntry {
    path: string;
    field: string;
    errorKeys: string[];
    errorLabel: string;
    rawErrors: ValidationErrors;
}

const joinPath = (parentPath: string, key: string): string => (parentPath ? `${parentPath}.${key}` : key);

/** Etichette leggibili per gli errori nativi di Angular (Validators.*), che non hanno la forma custom `{ status }`. */
const NATIVE_ERROR_LABELS: Record<string, (val: unknown) => string> = {
    required: () => REQUIRED_STATUS,
    email: () => 'Email non valida',
    pattern: () => 'Formato non valido',
    min: (val) => `Valore minimo: ${(val as { min: number }).min}`,
    max: (val) => `Valore massimo: ${(val as { max: number }).max}`,
    minlength: (val) => `Lunghezza minima: ${(val as { requiredLength: number }).requiredLength}`,
    maxlength: (val) => `Lunghezza massima: ${(val as { requiredLength: number }).requiredLength}`,
};

/** Oggetto errors di un control → stringa leggibile per la colonna "errore". */
const formatErrors = (errors: ValidationErrors): string =>
    Object.entries(errors)
        .map(([key, val]) => {
            if (val && typeof val === 'object' && 'status' in val) return `${key}: ${(val as { status: string }).status}`;
            if (key in NATIVE_ERROR_LABELS) return `${key}: ${NATIVE_ERROR_LABELS[key](val)}`;
            if (typeof val === 'object') return `${key}: ${JSON.stringify(val)}`;
            return `${key}: ${val}`;
        })
        .join(' | ');

/** Raccoglie ricorsivamente tutti i control con errors != null sotto `control`. */
export const collectFormErrors = (control: AbstractControl, parentPath = ''): FormErrorEntry[] => {
    const entries: FormErrorEntry[] = [];

    if (control.errors) {
        entries.push({
            path: parentPath,
            field: parentPath.split('.').pop() ?? parentPath,
            errorKeys: Object.keys(control.errors),
            errorLabel: formatErrors(control.errors),
            rawErrors: control.errors,
        });
    }

    if (control instanceof FormGroup) {
        Object.entries(control.controls).forEach(([key, child]) => entries.push(...collectFormErrors(child, joinPath(parentPath, key))));
    } else if (control instanceof FormArray) {
        control.controls.forEach((child, i) => entries.push(...collectFormErrors(child, joinPath(parentPath, `[${i}]`))));
    }

    return entries;
};
